import React, { useEffect, useRef } from 'react';
// eslint-disable-next-line max-len
import { ProspectProfileFormPreferences } from '@fortress-technology-solutions/fortress-component-library/Organisms_Fortress';
import { useLookingFor, useUnitTypeOptions } from './LookingFor.hook';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { Moment } from 'moment';
import useSelectedProperty from '../../../../hooks/useSelectedProperty';
import { PH_PROGRAM_NAME } from '../../../GeneralAffordable/constants';

type DateValue = Moment | null | undefined;
type NumericInputValue = number | string;
type Props = {
  intl: any,
  reduxFormOnChange: (name: string, value: any) => void,
  floorPlans: Object[],
  prospectInfoEditMode?: boolean,
  initialValues: {
    leaseTermInMonths: string,
    moveInDateTo: DateValue,
    moveInDateFrom: DateValue,
    nBeds?: NumericInputValue,
    nBedsArr: '' | string[],
    nBaths: NumericInputValue,
    nHalfBaths: NumericInputValue,
    noMoveInDate?: boolean,
    notWants: string,
    preferredFloorPlanId: string,
    priceFrom?: NumericInputValue,
    priceTo?: NumericInputValue,
    wants: string,
    prefersUnitAccessible: 'yes' | 'no',
    unitType: 'affordable' | 'conventional',
    affordableProgramsArr: string[],
  },
  applicationId?: string,
};

const LookingFor = ({
  intl,
  floorPlans,
  reduxFormOnChange,
  prospectInfoEditMode,
  initialValues,
  applicationId,
}: Props) => {
  const {
    propertySettingApplyWithoutAUnit,
    accessibleUnit,
    affordableNonAffordableProspect,
  } = useFlags();
  const selectedProperty = useSelectedProperty();
  const {
    formState,
    handleChange,
    leaseTermOptions,
    floorPlanOptions,
    nBedSelectOptions,
  } = useLookingFor({
    intl,
    initialValues,
    floorPlans,
    reduxFormOnChange,
    organizationId: selectedProperty.organizationId,
    propertyId: selectedProperty?.id,
    applicationId,
  });

  const pap = selectedProperty?.pap ?? [];
  const affordableProgramsOptions = pap.map((item) => {
    const name = item.masterAffordableProgram?.name;
    return { text: name, value: name };
  });

  const propertyClassName = selectedProperty?.propertyClass?.name ?? '';
  const hasAffordablePrograms = pap.length > 0;
  const isPHOnly =
    pap.length === 1 &&
    pap[0]?.masterAffordableProgram?.name === PH_PROGRAM_NAME;

  const unitTypeOptions = useUnitTypeOptions({
    propertyClassName,
    hasAffordablePrograms,
    intl,
  });

  const hasAutoFilled = useRef(false);
  useEffect(() => {
    if (isPHOnly && !hasAutoFilled.current && !initialValues.unitType) {
      hasAutoFilled.current = true;
      handleChange({ name: 'unitType', value: 'affordable' });
      handleChange({ name: 'affordableProgramsArr', value: [PH_PROGRAM_NAME] });
    }
  }, [isPHOnly, handleChange, initialValues.unitType]);
  return (
    <ProspectProfileFormPreferences
      intl={intl}
      containerSx={{
        margin: prospectInfoEditMode ? '0 8px 2rem 8px' : '0 -8px 2rem -8px',
      }}
      formState={formState}
      handleChange={handleChange}
      leaseTermOptions={leaseTermOptions}
      floorPlanOptions={floorPlanOptions}
      nBedSelectOptions={nBedSelectOptions}
      propertySettingApplyWithoutAUnit={propertySettingApplyWithoutAUnit}
      prospectInfoEditMode={prospectInfoEditMode}
      accessibleUnitFlag={accessibleUnit}
      affordableNonAffordableProspect={affordableNonAffordableProspect}
      affordableProgramsOptions={affordableProgramsOptions}
      unitTypeOptions={unitTypeOptions}
    />
  );
};

LookingFor.defaultProps = {
  initialValues: {
    leaseTermInMonths: '',
    moveInDateTo: null,
    moveInDateFrom: null,
    nBaths: '',
    nBedsArr: [],
    nHalfBaths: '',
    noMoveInDate: false,
    notWants: '',
    preferredFloorPlanId: '',
    priceFrom: '',
    priceTo: '',
    wants: '',
    prefersUnitAccessible: 'no',
    unitType: '',
    affordableProgramsArr: [],
  },
};

export default LookingFor;
